import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";

import { useCampaignContext } from "../../../context/CampaignContext";
import useAudienceToken from "../../../datahooks/useAudienceToken";
import useUpdateCampaign from "../../../datahooks/useUpdateCampaign";

import type {
  AudienceFacets,
  AudienceFilters,
  AudienceProfile,
  AudienceStats,
} from "../../../types";

const wsBaseUrl = import.meta.env.VITE_CORE_WS_URL;

export default function useAudienceProgress(
  filters: AudienceFilters,
  isMiningTargets?: boolean,
) {
  const socketRef = useRef<WebSocket | null>(null);

  const { audienceToken } = useAudienceToken();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const {
    id: campaignId,
    type: campaignType,
    custom_variable_keys: customVariables,
  } = useCampaignContext();

  const queryClient = useQueryClient();
  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { updateCachedCampaign } = useUpdateCampaign();

  const isList = campaignType === "list" || campaignType === "crm";

  const url = `${wsBaseUrl}campaigns/${campaignId}/audience?authorization=${audienceToken}`;

  const handlePeopleMessage = useCallback(
    (profiles: AudienceProfile[]) => {
      // On the Backend side, the custom variables are only added to the campaign, once there are some valid profiles
      // Because of that, for list campaign we need to invalidate the "Campaign Details" data in that case
      if (isList && profiles.length > 0 && customVariables.length === 0) {
        queryClient.invalidateQueries({
          queryKey: campaignsKeys.details(campaignId),
        });
      }

      const audienceSearchKey = campaignsKeys.audienceSearch(
        campaignId,
        filters,
      );
      const cachedAudienceSearch = queryClient.getQueryData(audienceSearchKey);

      if (!cachedAudienceSearch) return;

      queryClient.setQueryData(
        audienceSearchKey,
        (prev: {
          pages: { data: AudienceProfile[]; facets?: AudienceFacets }[];
          pageParams: number[];
        }) => {
          const prevAudience = prev.pages.flatMap((page) => page.data || []);
          const urnSet = new Set(prevAudience.map(({ urn }) => urn));

          const filteredNew = profiles.filter(({ urn }) => !urnSet.has(urn));

          const updatedPages = prev.pages.map((page, i) =>
            i === 0
              ? { ...page, data: [...filteredNew, ...(page.data || [])] }
              : page,
          );

          const totalUnique = new Set([
            ...prevAudience.map(({ urn }) => urn),
            ...filteredNew.map(({ urn }) => urn),
          ]).size;

          updateCachedCampaign(campaignId, (draft) => {
            draft.target_count = totalUnique;
          });

          return { ...prev, pages: updatedPages };
        },
      );
    },
    [campaignId, campaignType, customVariables.length, filters],
  );

  const handleStatusMessage = useCallback(
    (audienceStats: AudienceStats) => {
      const audienceStatsKey = campaignsKeys.audienceStats(campaignId);
      if (queryClient.getQueryData(audienceStatsKey)) {
        queryClient.setQueryData(audienceStatsKey, audienceStats);
      }
    },
    [campaignId],
  );

  const connect = useCallback(() => {
    const websocket = new WebSocket(url);
    socketRef.current = websocket;

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === "people") handlePeopleMessage(message.data);
      if (message.type === "status") handleStatusMessage(message);
    };
  }, [url, handlePeopleMessage, handleStatusMessage]);

  const reconnectSocket = useCallback(() => {
    socketRef.current?.close();
    connect();
  }, [connect]);

  useEffect(() => {
    if (!audienceToken || !isMiningTargets) return;

    connect();

    return () => socketRef.current?.close();
  }, [audienceToken, connect, isMiningTargets]);

  return { reconnectSocket };
}
